import React, { useState, useEffect } from 'react';
import { Calendar, Star, Church, Gift } from 'lucide-react';

const CatholicFeastsCalendar = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear] = useState(new Date().getFullYear());

  // Fêtes catholiques avec focus sur celles importantes au Bénin
  const catholicFeasts = {
    10: [ // Novembre (mois actuel)
      {
        date: 1,
        name: "Toussaint",
        description: "Fête de tous les saints. Jour férié au Bénin",
        type: "solennity",
        color: "text-yellow-600",
        icon: "👑"
      },
      {
        date: 2,
        name: "Commémoration des fidèles défunts",
        description: "Prière pour nos ancêtres et défunts",
        type: "commemoration",
        color: "text-amber-500",
        icon: "🕯️"
      },
      {
        date: 21,
        name: "Présentation de la Vierge Marie",
        description: "Fête mariale célébrée au Bénin",
        type: "feast",
        color: "text-blue-600",
        icon: "👸"
      },
      {
        date: 30,
        name: "Saint André",
        description: "Patron des pêcheurs (important pour les côtes béninoises)",
        type: "feast",
        color: "text-green-600",
        icon: "🎣"
      }
    ],
    11: [ // Décembre
      {
        date: 8,
        name: "Immaculée Conception",
        description: "Fête patronale de nombreuses paroisses au Bénin",
        type: "solennity",
        color: "text-blue-600",
        icon: "🌟"
      },
      {
        date: 25,
        name: "Nativité du Seigneur",
        description: "Noël - Grande fête familiale au Bénin",
        type: "solemnity",
        color: "text-red-600",
        icon: "🎄"
      }
    ],
    0: [ // Janvier
      {
        date: 1,
        name: "Sainte Marie, Mère de Dieu",
        description: "Jour de l'An - Fête mariale au Bénin",
        type: "solennity",
        color: "text-blue-600",
        icon: "👑"
      },
      {
        date: 6,
        name: "Épiphanie",
        description: "Fête des Rois Mages",
        type: "solemnity",
        color: "text-yellow-600",
        icon: "⭐"
      }
    ]
  };

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const getCurrentMonthFeasts = () => {
    return catholicFeasts[currentMonth] || [];
  };

  const nextMonth = () => {
    setCurrentMonth((prev) => (prev + 1) % 12);
  };

  const prevMonth = () => {
    setCurrentMonth((prev) => (prev - 1 + 12) % 12);
  };

  return (
    <section className="py-16 bg-gradient-to-br from-yellow-50 via-orange-50 to-red-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <Church className="w-8 h-8 text-fsf-accent" />
            <h2 className="text-3xl md:text-4xl font-bold text-fsf-primary font-serif">
              Calendrier liturgique béninois
            </h2>
            {/* <span className="text-3xl">🇧🇯</span> */}
          </div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto text-justify">
            Découvrez les fêtes catholiques importantes célébrées dans nos communautés au Bénin
          </p>
        </div>

        {/* Sélecteur de mois */}
        <div className="flex items-center justify-center space-x-6 mb-8">
          <button
            onClick={prevMonth}
            className="p-2 rounded-lg bg-fsf-primary text-white hover:bg-fsf-secondary transition-colors duration-200"
          >
            ←
          </button>
          <div className="text-center">
            <h3 className="text-2xl font-bold text-fsf-primary font-serif">
              {monthNames[currentMonth]} {currentYear}
            </h3>
          </div>
          <button
            onClick={nextMonth}
            className="p-2 rounded-lg bg-fsf-primary text-white hover:bg-fsf-secondary transition-colors duration-200"
          >
            →
          </button>
        </div>

        {/* Liste des fêtes du mois */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {getCurrentMonthFeasts().map((feast, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 border-l-4 border-fsf-accent"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-fsf-accent to-fsf-gold rounded-lg flex items-center justify-center text-2xl">
                  {feast.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <Calendar className="w-4 h-4 text-fsf-accent" />
                    <span className="font-bold text-fsf-accent">
                      {feast.date} {monthNames[currentMonth]}
                    </span>
                  </div>
                  <h4 className={`text-lg font-bold ${feast.color} mb-2 font-serif`}>
                    {feast.name}
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed text-justify">
                    {feast.description}
                  </p>
                  <div className="mt-3">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                      feast.type === 'solemnity' ? 'bg-red-100 text-red-700' :
                      feast.type === 'feast' ? 'bg-blue-100 text-blue-700' :
                      'bg-amber-100 text-amber-600'
                    }`}>
                      {feast.type === 'solemnity' ? 'Solennité' : 
                       feast.type === 'feast' ? 'Fête' : 'Commémoration'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Section spéciale pour les traditions béninoises */}
        <div className="mt-16 bg-gradient-to-r from-green-600 to-yellow-500 text-white rounded-2xl shadow-2xl p-8 max-w-4xl mx-auto">
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-4 font-serif flex items-center justify-center space-x-3">
              <span>🌍</span>
              <span>Traditions catholiques du Bénin</span>
              <span>🙏</span>
            </h3>
            <div className="grid md:grid-cols-2 gap-6 text-left">
              <div className="bg-white/10 rounded-lg p-4">
                <h4 className="font-bold text-yellow-200 mb-2">🎭 Fêtes patronales</h4>
                <p className="text-green-100 text-sm">
                  Chaque paroisse célèbre son saint patron avec des processions colorées 
                  mêlant traditions africaines et liturgie catholique.
                </p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <h4 className="font-bold text-yellow-200 mb-2">🥁 Musique liturgique</h4>
                <p className="text-green-100 text-sm">
                  Les messes sont rythmées par les tambours traditionnels et les chants 
                  en langues locales (Fon, Yoruba, Bariba).
                </p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <h4 className="font-bold text-yellow-200 mb-2">👨‍👩‍👧‍👦 Bénédictions familiales</h4>
                <p className="text-green-100 text-sm">
                  Tradition de bénir les unions et les naissances selon les rites 
                  catholiques adaptés à la culture béninoise.
                </p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <h4 className="font-bold text-yellow-200 mb-2">🌾 Fête des récoltes</h4>
                <p className="text-green-100 text-sm">
                  Action de grâce pour les récoltes avec offrandes des premiers fruits 
                  selon les traditions agricoles locales.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CatholicFeastsCalendar;